/**
 * @engine/core - Game View Renderer
 *
 * Renders the game view through a scene camera with:
 * - Post-processing support
 * - FPS throttling in edit mode
 * - Camera component integration
 *
 * Shared between editor (GameView) and player (HTML5 export).
 *
 * @module @engine/core
 */

import {
  createRenderContext,
  Camera2D,
  ShapeBatch,
  SpriteBatch,
  TextureManager,
  PostProcessStack,
  OffscreenTarget,
  createEffect,
  type PostProcessEffect,
  type PostProcessEffectDesc,
  type IRenderContext,
  type GPUBackend,
  type TextureHandle,
  type BufferHandle,
  type DeferredLightingPipeline,
  type LightManager,
  type LightEmittingSystem,
  type ShadowCaster2D,
  type Material,
  graphicsConfig,
} from '@engine/render2d';
import { Camera2D as Camera2DComponent } from '../components';
import type { Level } from '../Level';
import type { CameraInstance } from '../Camera';
import { BaseViewportRenderer } from './BaseViewportRenderer';
import { SceneSpriteCollector, type SpriteCollectorDeps } from './scene/SceneSpriteCollector';
import { RenderPipeline, type ViewportPass, type ViewportPassContext } from './RenderPipeline';
import {
  CollectLightsPass,
  InjectVfxLightsPass,
  CullLightsPass,
  ComputeShadowsPass,
} from './passes/LightingPasses';
import {
  GBufferOpaquePass,
  DeferredAccumPass,
  ForwardPlusOpaquePass,
  UnlitOpaquePass,
  TransparentSpritesPass,
  ParticlesPass,
} from './passes/ContentPasses';
import { PostProcessPass } from './passes/PostProcessPasses';
import { FPSLimiter } from './ViewportTypes';
import type { ViewportRenderRequest } from './ViewportTypes';

/** Which render path's pass pipeline to run, chosen per frame from graphicsConfig.renderPath. */
type RenderPath = 'deferred' | 'forward+' | 'unlit';

/**
 * Game view renderer - renders through scene camera
 */
export class GameViewRenderer extends BaseViewportRenderer {
  // Post-processing
  private postProcessStack: PostProcessStack | null = null;
  private sceneTarget: OffscreenTarget | null = null;

  // Camera reference (from scene)
  private activeCamera: CameraInstance | null = null;

  // Current mode
  private isPlayMode = false;

  // Background color as linear floats (from camera or default). Kept unquantized — an HDR clear
  // must not round-trip through 8-bit.
  private backgroundColor: [number, number, number] = [0, 0, 0];

  // Per-camera HDR flag (Camera2D.hdr, default true). When on, the scene always renders to the HDR
  // offscreen target and resolves through the post stack — with zero user effects that's a single
  // builtin tonemap pass.
  private cameraHdr = true;

  // Builtin final tonemap for HDR cameras. Lives OUTSIDE postProcessStack.effects so reconcile()
  // never disposes it; injected into the PostProcess pass per frame.
  private builtinTonemap: PostProcessEffect | null = null;

  // Backend preference
  private preferredBackend: GPUBackend = 'webgpu';

  // Shared render context (for resource sharing between Editor and Game views)
  private sharedContext: IRenderContext | null = null;

  // Time tracking
  private time = 0;

  // Texture resolver
  private textureResolver: ((spriteId: string) => Promise<string | null>) | null = null;

  // Material resolver — guid → cached Material (built by the host: editor compiles .slang/.wgsl on the
  // shared context; player supplies precompiled). Null result = unresolved/loading → falls back to plain draw.
  private materialResolver: ((guid: string) => Material | null) | null = null;

  // Lighting collect callback (always-on)
  private lightingCollectCallback: ((camera: Camera2D, width: number, height: number) => void) | null = null;

  // Deferred lighting callback — returns resources if deferred path is available
  private deferredLightingCallback: ((width: number, height: number) => {
    pipeline: DeferredLightingPipeline;
    lightManager: LightManager;
    collect: (camera: Camera2D, w: number, h: number) => void;
    iesProfileBuffer?: BufferHandle | null;
  } | null) | null = null;

  // VFX render callback
  private vfxRenderCallback: ((ctx: IRenderContext, viewProjection: Float32Array, isHdrTarget: boolean) => void) | null = null;

  // VFX light injection callback — returns light-emitting particle systems
  private vfxLightInjectCallback: (() => LightEmittingSystem[]) | null = null;

  // Shadow caster collection callback
  private shadowCasterCollectCallback: (() => ShadowCaster2D[]) | null = null;

  // Texture cache
  private textureCache = new Map<string, TextureHandle>();

  // Debug flag
  private hasLoggedFirstRender = false;

  // Post-process sync tracking
  private lastPostProcessHash = '';

  // Shared sprite collection + draw core (was duplicated with EditorViewRenderer)
  private spriteCollector = new SceneSpriteCollector();

  // Render pass pipelines, lazily built + cached per render path.
  private _pipelines: Partial<Record<RenderPath, RenderPipeline>> = {};
  private _blackboard = new Map<string, unknown>();

  // Externally injected passes (e.g. @engine/ui UIPass — core can't depend on ui). Appended to every
  // built pipeline; added retroactively to already-built ones.
  private externalPasses: ViewportPass[] = [];

  constructor(backend: GPUBackend = 'webgpu') {
    super();
    this.preferredBackend = backend;
  }

  /** Lazily build + cache the pass pipeline for a render path. */
  private pipelineFor(path: RenderPath): RenderPipeline {
    let pipeline = this._pipelines[path];
    if (!pipeline) {
      pipeline = this.buildPipeline(path);
      this._pipelines[path] = pipeline;
    }
    return pipeline;
  }

  /**
   * Build the game pass list for a render path (shared @engine/core passes — no editor overlays). Stage order
   * is unified Lighting-first; the final PostProcess-stage pass resolves the HDR scene target through the post
   * stack to the backbuffer (no-op when no effects are active — see {@link PostProcessPass}).
   */
  private buildPipeline(path: RenderPath): RenderPipeline {
    const p = new RenderPipeline();
    if (path === 'unlit') {
      p.add(new CollectLightsPass(), new UnlitOpaquePass());
    } else {
      p.add(new CollectLightsPass(), new InjectVfxLightsPass(), new CullLightsPass(), new ComputeShadowsPass());
      if (path === 'deferred') {
        p.add(new GBufferOpaquePass(), new DeferredAccumPass(), new TransparentSpritesPass());
      } else {
        p.add(new ForwardPlusOpaquePass());
      }
    }
    p.add(new ParticlesPass());
    p.add(new PostProcessPass(
      () => this.postProcessStack,
      () => this.sceneTarget,
      () => (this.cameraHdr ? this.builtinTonemap : null),
    ));
    if (this.externalPasses.length) p.add(...this.externalPasses);
    return p;
  }

  /**
   * Inject passes from outside core (subsystem-owned overlays like the game UI). Passes are stage-sorted
   * by the pipeline, so injection order doesn't matter. Passes must lazy-init their GPU resources in
   * execute() — pipelines may already be built and initialized when this is called.
   */
  addPass(...passes: ViewportPass[]): void {
    this.externalPasses.push(...passes);
    for (const pipeline of Object.values(this._pipelines)) pipeline?.add(...passes);
  }

  /**
   * Set play mode state
   */
  setPlayMode(enabled: boolean): void {
    this.isPlayMode = enabled;
  }

  /**
   * Set shared context for resource sharing (must be called before initialize)
   * This allows VFX and other resources created on EditorView to work in GameView.
   * @param ctx - IRenderContext from EditorView
   */
  setSharedContext(ctx: IRenderContext | null): void {
    this.sharedContext = ctx;
  }

  /**
   * Check if should render this frame (respects FPS limiting)
   * @deprecated FPS limiting is deprecated and disabled for smoother editor experience
   */
  shouldRenderFrame(): boolean {
    return true;
  }

  /**
   * Mark frame as rendered
   * @deprecated No longer needed since FPS limiting is disabled
   */
  markFrameRendered(): void {
  }

  /**
   * Set active camera from scene
   */
  setActiveCamera(camera: CameraInstance | null): void {
    this.activeCamera = camera;
  }

  /**
   * Get post-processing stack
   */
  getPostProcessStack(): PostProcessStack | null {
    return this.postProcessStack;
  }

  /**
   * Set background color (packed 0xRRGGBB — legacy external API; camera data feeds floats directly)
   */
  setBackgroundColor(color: number): void {
    this.backgroundColor = [
      ((color >> 16) & 0xFF) / 255,
      ((color >> 8) & 0xFF) / 255,
      (color & 0xFF) / 255,
    ];
  }

  /**
   * Set texture resolver for loading sprites by ID/GUID
   */
  setTextureResolver(resolver: (spriteId: string) => Promise<string | null>): void {
    this.textureResolver = resolver;
  }

  /**
   * Set the material resolver: a guid → cached Material lookup. The host builds materials (editor compiles
   * .slang/.wgsl on the shared render context; player supplies precompiled) so this renderer stays headless.
   * Without it, sprites with a material fall back to the plain textured path.
   */
  setMaterialResolver(resolver: (guid: string) => Material | null): void {
    this.materialResolver = resolver;
  }

  /**
   * Set lighting collect callback
   */
  setLightingCollectCallback(callback: (camera: Camera2D, width: number, height: number) => void): void {
    this.lightingCollectCallback = callback;
  }

  /**
   * Set deferred lighting callback. Returns deferred resources if available,
   * enabling GBuffer → light accumulation render path.
   */
  setDeferredLightingCallback(callback: (width: number, height: number) => {
    pipeline: DeferredLightingPipeline;
    lightManager: LightManager;
    collect: (camera: Camera2D, w: number, h: number) => void;
    iesProfileBuffer?: BufferHandle | null;
  } | null): void {
    this.deferredLightingCallback = callback;
  }

  /**
   * Set VFX render callback
   */
  setVFXRenderCallback(callback: (ctx: IRenderContext, viewProjection: Float32Array, isHdrTarget: boolean) => void): void {
    this.vfxRenderCallback = callback;
  }

  /**
   * Set VFX light injection callback.
   * Called between LightManager.end() and LightCullPass.cull() to gather
   * light-emitting particle systems.
   */
  setVFXLightInjectCallback(callback: () => LightEmittingSystem[]): void {
    this.vfxLightInjectCallback = callback;
  }

  /**
   * Set shadow caster collection callback.
   * Returns all active ShadowCaster2D shapes from the current scene.
   */
  setShadowCasterCollectCallback(callback: () => ShadowCaster2D[]): void {
    this.shadowCasterCollectCallback = callback;
  }

  /**
   * Resolve texture GUID to handle
   */
  async resolveTextureToHandle(guid: string): Promise<TextureHandle | null> {
    if (!this.ctx || !this.textureManager) return null;

    // Check cache
    if (this.textureCache.has(guid)) {
      return this.textureCache.get(guid) ?? null;
    }

    // Resolve URL
    if (!this.textureResolver) return null;
    const url = await this.textureResolver(guid);
    if (!url) return null;

    // Load texture
    try {
      const handle = await this.textureManager.loadUrl(guid, url);
      this.textureCache.set(guid, handle);
      return handle;
    } catch (e) {
      console.error(`[GameViewRenderer] Failed to load texture ${guid}:`, e);
      return null;
    }
  }

  /**
   * Release a texture: drop it from the cache and free its GPU resource. Called by the level
   * lifecycle's ResourceManager when a texture is no longer referenced by any loaded level.
   * No-op for the "loading" sentinel handle (0) and for textures that were never resolved.
   */
  releaseTexture(guid: string): void {
    const handle = this.textureCache.get(guid);
    this.textureCache.delete(guid);
    if (handle !== undefined && handle !== (0 as TextureHandle) && this.textureManager) {
      this.textureManager.destroy(guid);
    }
  }

  protected async onInitialize(canvas: HTMLCanvasElement): Promise<void> {
    // Create render context (with shared context if available for resource sharing)
    this.ctx = await createRenderContext({
      canvas,
      backend: this.preferredBackend,
      antialias: false,
      powerPreference: 'high-performance',
      sharedContext: this.sharedContext ?? undefined,
    });

    // Create camera
    this.camera = new Camera2D(canvas.width, canvas.height);

    // Create batchers
    this.shapeBatch = new ShapeBatch(this.ctx, { maxVertices: 50000 });
    this.spriteBatch = new SpriteBatch(this.ctx, { maxSprites: 10000 });

    // Create texture manager
    this.textureManager = new TextureManager(this.ctx);

    // Create post-processing stack
    this.postProcessStack = new PostProcessStack();
    this.postProcessStack.initialize(this.ctx, canvas.width, canvas.height);

    // Create offscreen target for scene rendering (HDR for bloom support)
    this.sceneTarget = new OffscreenTarget(this.ctx, canvas.width, canvas.height, false, true);

    // Builtin HDR→LDR resolve (ACES default). Only used when the camera is HDR and the stack has
    // no user tonemap; owned here, never part of postProcessStack.effects.
    this.builtinTonemap = createEffect('tonemap');
    if (this.builtinTonemap) {
      this.builtinTonemap.initialize(this.ctx);
      this.builtinTonemap.resize(this.ctx, canvas.width, canvas.height);
    }

    console.log(`[GameViewRenderer] Initialized with ${this.ctx.backend}`);
  }

  override resize(width: number, height: number): void {
    super.resize(width, height);

    // Resize post-processing buffers
    this.postProcessStack?.resize(width, height);
    if (this.ctx && this.builtinTonemap) this.builtinTonemap.resize(this.ctx, width, height);

    // Resize scene target
    this.sceneTarget?.resize(width, height);
  }

  protected onRender(request: ViewportRenderRequest): number {
    if (!this.ctx || !this.camera) {
      console.warn('[GameViewRenderer] onRender: no ctx or camera');
      return 0;
    }

    const { width, height, level, time, deltaTime } = request;
    this.time = time;

    // Animation clock for material shaders (uTime). Fed once per frame, before any draw.
    this.spriteBatch?.setTime(time);

    // Reset GPU buffer cursors for new frame (prevents WebGPU buffer overwrites
    // when multiple begin/end cycles write to the same buffer region)
    this.spriteBatch?.startFrame();
    this.shapeBatch?.startFrame();

    // Check FPS limiting
    if (!this.shouldRenderFrame() && !request.isPlayMode) {
      return 0;
    }

    // Update play mode state
    this.isPlayMode = request.isPlayMode;

    // ALWAYS log when activeCamera changes or has post-process data
    if (this.activeCamera && level) {
      // Find camera entity by numeric ID
      let cameraEntity = null;
      for (const go of level.getAllObjects()) {
        if (go.id === this.activeCamera.entityId) {
          cameraEntity = go;
          break;
        }
      }

      if (cameraEntity) {

        // Apply transform position to camera
        const t = cameraEntity.transform;
        this.camera.setPosition(t.position.x, t.position.y);

        // Read Camera2D component for additional settings
        const camData = cameraEntity.getComponentData('core.Camera2D');
        if (camData) {
            // Apply ortho size (always set, with default fallback)
            // orthographicSize is half the vertical size of the view in world units
            const orthoSize = camData.orthographicSize > 0
              ? camData.orthographicSize
              : 5; // default from component definition
            const aspect = width / height;
            const halfHeight = orthoSize;
            const halfWidth = halfHeight * aspect;
            this.camera.setViewport(halfWidth * 2, halfHeight * 2);

            // Apply background color (kept as floats — no 8-bit quantization of the clear)
            const bg = camData.backgroundColor;
            this.backgroundColor = [bg.r ?? 0, bg.g ?? 0, bg.b ?? 0];

            // Per-camera HDR flag; absent key (pre-HDR levels) = ON
            this.cameraHdr = camData.hdr !== false;

            // Sync post-processing effects from component data
            this.syncPostProcessEffects(camData);
        }
      } else if (!this.hasLoggedFirstRender) {
        console.log(`[GameViewRenderer] Camera entity NOT FOUND for id=${this.activeCamera.entityId}`);
      }
    }

    // Get background color
    const [bgR, bgG, bgB] = this.backgroundColor;

    // Check if post-processing is active
    const hasPostProcess = this.postProcessStack?.hasActiveEffects ?? false;

    // ── Render path + color-target selection ──
    const deferred = this.deferredLightingCallback?.(width, height) ?? null;
    const path: RenderPath =
      graphicsConfig.renderPath === 'deferred' && deferred ? 'deferred'
      : graphicsConfig.renderPath === 'forward+' && deferred ? 'forward+'
      : 'unlit';

    // HDR camera or active post effects → render the scene into the HDR offscreen target and resolve it
    // through the post stack to the backbuffer (an HDR camera with zero effects resolves via the builtin
    // tonemap). Camera HDR off + no effects → legacy direct-to-backbuffer LDR path.
    const usePostProcess = hasPostProcess && !!this.sceneTarget && !!this.postProcessStack;
    const useSceneTarget = usePostProcess ||
      (this.cameraHdr && !!this.builtinTonemap && !!this.sceneTarget && !!this.postProcessStack);
    const colorTarget = useSceneTarget ? this.sceneTarget!.handle : null;
    const isHdrTarget = useSceneTarget;

    // Clear the color target with the camera background (establishes the render pass).
    const ctx = this.ctx;
    ctx.setRenderTarget(colorTarget);
    ctx.clear(bgR, bgG, bgB, 1);
    ctx.setViewport(0, 0, width, height);

    this._blackboard.clear();
    const c: ViewportPassContext = {
      ctx,
      camera: this.camera,
      level,
      width,
      height,
      time,
      deltaTime,
      isPlayMode: request.isPlayMode,
      isHdrTarget,
      colorTarget,
      sprites: this.spriteCollector,
      spriteDeps: this.spriteDeps(),
      lighting: deferred,
      callbacks: {
        shadowCasterCollect: this.shadowCasterCollectCallback,
        vfxLightInject: this.vfxLightInjectCallback,
        vfxRender: this.vfxRenderCallback,
        isPlaying: true, // GameView always renders particles (legacy renderVFX was unconditional)
      },
      beginColorPass: () => {
        ctx.setRenderTarget(colorTarget);
        ctx.setViewport(0, 0, width, height);
      },
      blackboard: this._blackboard,
    };

    // The HDR scene target is resolved through the post stack → backbuffer by the PostProcess-stage pass
    // (profiled, no-op when no effects are active).
    this.pipelineFor(path).execute(c);
    const spriteCount = level ? this.spriteCollector.all.length : 0;

    this.markFrameRendered();
    this.hasLoggedFirstRender = true;

    return spriteCount;
  }

  /**
   * Build the per-frame collector deps. Texture resolution goes through the game's baked/lazy cache;
   * material resolution through the injected host resolver (editor compiles, player supplies precompiled).
   */
  private spriteDeps(): SpriteCollectorDeps {
    return {
      ctx: this.ctx!,
      camera: this.camera!,
      spriteBatch: this.spriteBatch!,
      resolveTexture: this._resolveTextureForCollector,
      resolveMaterial: this._resolveMaterialForCollector,
    };
  }

  private _resolveTextureForCollector = (spriteId: string): TextureHandle | null => {
    const cached = this.textureCache.get(spriteId);
    if ((cached === undefined || cached <= 0) && this.textureResolver) this.loadTextureAsync(spriteId);
    return cached ?? null;
  };

  private _resolveMaterialForCollector = (guid: string): Material | null => {
    return this.materialResolver?.(guid) ?? null;
  };

  /**
   * Async texture loading helper
   */
  private loadTextureAsync(spriteId: string): void {
    if (this.textureCache.has(spriteId)) return;
    if (!this.textureResolver || !this.textureManager) return;

    // Mark as loading (set to invalid handle)
    this.textureCache.set(spriteId, 0 as TextureHandle);

    this.textureResolver(spriteId)
      .then(url => {
        if (url && this.textureManager) {
          return this.textureManager.loadUrl(spriteId, url);
        }
        return null;
      })
      .then(handle => {
        if (handle) {
          this.textureCache.set(spriteId, handle);
        } else {
          this.textureCache.delete(spriteId);
        }
      })
      .catch(err => {
        console.warn(`[GameViewRenderer] Failed to load texture ${spriteId}:`, err);
        this.textureCache.delete(spriteId);
      });
  }

  /**
   * Sync post-processing effects from camera component data
   */
  private syncPostProcessEffects(cameraData: Camera2DComponent): void {
    if (!this.postProcessStack) {
      console.warn('[GameViewRenderer] syncPostProcessEffects: no postProcessStack');
      return;
    }

    // Default to enabled if not explicitly set to false
    const ppEnabled = cameraData.postProcessEnabled !== false;
    const ppStack = cameraData.postProcessStack as Array<{
      type: string;
      enabled: boolean;
      intensity: number;
      order: number;
      params: Record<string, unknown>;
    }> | undefined;

    // Cheap change detection so we only touch the stack when the serialized settings change.
    const hash = JSON.stringify({ ppEnabled, ppStack: ppStack ?? [] });
    if (hash === this.lastPostProcessHash) {
      return;
    }
    this.lastPostProcessHash = hash;

    // Reconcile in place: reuse effect instances, push param/enabled/order changes onto them, and
    // add/remove/reorder only when the SET of effects changes. This replaces the old clear()+recreate,
    // which disposed and rebuilt every effect (shaders, pipelines, bloom's whole mip target chain)
    // on every change — including an animated parameter, which changed the hash every single frame.
    const descs: PostProcessEffectDesc[] =
      ppEnabled && Array.isArray(ppStack) ? ppStack : [];
    this.postProcessStack.reconcile(descs, createEffect);
  }

  protected onDispose(): void {
    for (const p of Object.values(this._pipelines)) p?.dispose();
    this._pipelines = {};
    if (this.ctx && this.builtinTonemap) this.builtinTonemap.dispose(this.ctx);
    this.builtinTonemap = null;
    this.postProcessStack?.dispose();
    this.sceneTarget?.destroy();

    this.postProcessStack = null;
    this.sceneTarget = null;
    this.activeCamera = null;
  }
}
